import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import OfferStrip from '../components/common/OfferStrip';
import FilingHero from '../components/drhp-filing/FilingHero';
import FilingTerms from '../components/drhp-filing/FilingTerms';
import FilingFinancials from '../components/drhp-filing/FilingFinancials';
import FilingParties from '../components/drhp-filing/FilingParties';
import FilingDisclosures from '../components/drhp-filing/FilingDisclosures';
import FilingSidebar from '../components/drhp-filing/FilingSidebar';
import { fetchDrhpFiling, selectDrhpFiling } from '../store/slices/drhpFilingSlice';

const Skeleton = () => (
  <div className="space-y-4 sm:space-y-5 animate-pulse">
    <div className="h-36 rounded-2xl bg-white border border-slate-200/90" />
    <div className="grid grid-cols-1 min-[1200px]:grid-cols-[minmax(0,1fr)_340px] gap-4 sm:gap-5">
      <div className="space-y-4">
        <div className="h-48 rounded-2xl bg-white border border-slate-200/90" />
        <div className="h-64 rounded-2xl bg-white border border-slate-200/90" />
      </div>
      <div className="h-80 rounded-2xl bg-white border border-slate-200/90" />
    </div>
  </div>
);

/**
 * One DRHP/RHP filing in full: GET /api/core/ipos/drhp-tracker/{id}, reached by
 * clicking a company in the filings tracker.
 */
export const DrhpFilingPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const filing = useSelector((state) => selectDrhpFiling(state, id));
  const status = useSelector((state) => state.drhpFiling.status);
  const error = useSelector((state) => state.drhpFiling.error);
  const isLoading = useSelector((state) => state.drhpFiling.loadingId === String(id));

  useEffect(() => {
    if (id) dispatch(fetchDrhpFiling({ id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (!filing) return undefined;
    const previousTitle = document.title;
    document.title = `${filing.company} DRHP Filing | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, [filing]);

  const failed = !filing && !isLoading && status === 'failed';

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        <OfferStrip slotId={['drhp-filings-top', 'leaderboard']} />

        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-4 sm:space-y-5">
          {(isLoading || (!filing && !failed)) && <Skeleton />}

          {failed && (
            <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-8 sm:p-12 text-center">
              <AlertCircle className="w-10 h-10 mx-auto text-rose-400" aria-hidden="true" />
              <h1 className="mt-3 text-lg font-extrabold text-slate-900">This filing could not be loaded</h1>
              <p className="mt-1.5 text-sm text-slate-500">{error}</p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => dispatch(fetchDrhpFiling({ id, force: true }))}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#008080] hover:bg-[#006666] text-xs font-bold text-white transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Try again
                </button>
                <Link
                  to="/drhp-filings"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-teal-200 hover:text-[#008080] transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to all filings
                </Link>
              </div>
            </div>
          )}

          {filing && !isLoading && (
            <>
              <FilingHero filing={filing} />

              {!filing.hasAnalysis && (
                <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 leading-relaxed">
                  <AlertCircle className="w-4 h-4 mt-px flex-shrink-0" aria-hidden="true" />
                  Only the filing record is published for this company so far. Issue structure, financials and disclosures
                  appear here once its document extraction is released.
                </p>
              )}

              <div className="grid grid-cols-1 min-[1200px]:grid-cols-[minmax(0,1fr)_340px] gap-4 sm:gap-5 items-start">
                <div className="space-y-4 sm:space-y-5 min-w-0">
                  <FilingTerms
                    terms={filing.terms}
                    quota={filing.quota}
                    quotaNote={filing.quotaNote}
                    biddingDates={filing.biddingDates}
                  />
                  <FilingFinancials financials={filing.financials} kpis={filing.kpis} />
                  <FilingDisclosures
                    overview={filing.overview}
                    industry={filing.industry}
                    objects={filing.objects}
                    risks={filing.risks}
                    litigation={filing.litigation}
                    litigationNote={filing.litigationNote}
                  />
                  <FilingParties parties={filing.parties} />
                </div>

                <FilingSidebar filing={filing} />
              </div>

              <Link
                to="/drhp-filings"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#008080] transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to all DRHP filings
              </Link>
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default DrhpFilingPage;
